/**
 * Trunnion AI - Save Workflow Dialog
 */

import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ClearanceSelector } from '../panels/ClearanceSelector';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';
import type { ClassificationLevel } from '@/types';

interface SaveWorkflowDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (meta: {
    name: string;
    description: string;
    classification: ClassificationLevel;
  }) => void;
  loading?: boolean;
}

export function SaveWorkflowDialog({
  open,
  onClose,
  onSave,
  loading,
}: SaveWorkflowDialogProps) {
  const workflowName = useWorkflowDesignerStore((s) => s.workflowName);
  const workflowDescription = useWorkflowDesignerStore(
    (s) => s.workflowDescription,
  );
  const workflowClassification = useWorkflowDesignerStore(
    (s) => s.workflowClassification,
  );

  const [name, setName] = useState(workflowName);
  const [description, setDescription] = useState(workflowDescription);
  const [classification, setClassification] = useState(workflowClassification);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: description.trim(), classification });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Save Workflow"
      description="Save your workflow configuration"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={loading} disabled={!name.trim()}>
            Save Workflow
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Workflow Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter workflow name"
        />
        <div>
          <label className="text-sm font-medium text-trunnion-slate-200">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1.5 w-full rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 px-3 py-2 text-sm text-white placeholder-trunnion-slate-400 focus:outline-none focus:ring-2 focus:ring-trunnion-gold-500 resize-none"
            placeholder="Describe the purpose of this workflow..."
          />
        </div>
        <ClearanceSelector
          value={classification}
          onChange={setClassification}
        />
      </div>
    </Modal>
  );
}
